import { useState } from 'react';
import type { FormEvent } from 'react';
import { Logo } from '../shell/Header';
import { Button } from '../components/Button';

/**
 * The auth card, ported from views/login.ejs and views/register.ejs.
 *
 * The markup is the EJS markup — `.login-wrap`, `.login-card`, `.login-logo`, `.login-title`,
 * `.login-sub`, `.login-foot` — because those rules were moved into public/app.css and this page has no
 * stylesheet of its own. The form posts to /api/v1/auth/*, which calls the same services/auth.js the EJS
 * form posted to, so the invite check, the lockout and the dev impersonation cannot differ between them.
 *
 * On success the page RELOADS rather than routing: the session cookie is new, and every query the shell
 * holds (whoami above all) was answered while signed out. A reload is one round-trip and no cache
 * archaeology.
 */
type AuthResult = { userId: number; role?: string };

async function post(path: string, body: Record<string, string>): Promise<{ ok: boolean; error?: string; data?: AuthResult }> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  let json: { ok: boolean; error?: string; data?: AuthResult } | null = null;
  try {
    json = await res.json();
  } catch {
    json = null;
  }
  if (!json) return { ok: false, error: `HTTP ${res.status}` };
  return json;
}

function AuthCard({ title, sub, children, foot }: { title: string; sub: string; children: React.ReactNode; foot: React.ReactNode }) {
  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="login-logo">
          <Logo animate />
        </div>
        <h1 className="login-title">{title}</h1>
        <p className="login-sub">{sub}</p>
        {children}
        <p className="login-foot muted">{foot}</p>
      </div>
    </div>
  );
}

export function LoginPage({ next }: { next?: string }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // A form post that fails redirects back here with ?error= (the SPA server answers POST /login the way
  // the EJS server does), so the message a reader sees has to survive the redirect.
  const [error, setError] = useState<string | null>(() => new URLSearchParams(window.location.search).get('error'));
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const r = await post('/api/v1/auth/login', { email, password, next: next || '' });
    if (!r.ok) {
      setError(r.error || 'Could not sign in.');
      setBusy(false);
      return;
    }
    window.location.assign(next || '/');
  };

  return (
    <AuthCard title="Welcome back" sub="Log in to your portfolio" foot={<>No account? <a href="/register">Register</a></>}>
      {error ? <div className="error" style={{ marginBottom: 14 }}>{error}</div> : null}
      <form onSubmit={submit}>
        <label htmlFor="email">Email</label>
        <input id="email" name="email" type="email" required autoComplete="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
        <label htmlFor="password">Password</label>
        <input id="password" name="password" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        <Button type="submit" block disabled={busy}>
          {busy ? 'Signing in…' : 'Log in'}
        </Button>
      </form>
    </AuthCard>
  );
}

export function RegisterPage() {
  const [invite, setInvite] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // A form post that fails redirects back here with ?error= (the SPA server answers POST /login the way
  // the EJS server does), so the message a reader sees has to survive the redirect.
  const [error, setError] = useState<string | null>(() => new URLSearchParams(window.location.search).get('error'));
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const r = await post('/api/v1/auth/register', { invite_key: invite, email, password });
    if (!r.ok) {
      setError(r.error || 'Could not create the account.');
      setBusy(false);
      return;
    }
    window.location.assign('/');
  };

  return (
    <AuthCard title="Create your account" sub="Start tracking your portfolio" foot={<>Already registered? <a href="/login">Log in</a></>}>
      {error ? <div className="error" style={{ marginBottom: 14 }}>{error}</div> : null}
      <form onSubmit={submit}>
        <label htmlFor="invite_key">Beta invite key</label>
        <input id="invite_key" name="invite_key" type="text" required placeholder="Invite key" autoComplete="off" value={invite} onChange={(e) => setInvite(e.target.value)} />
        <label htmlFor="email">Email</label>
        <input id="email" name="email" type="email" required autoComplete="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
        <label htmlFor="password">Password (min 8 characters)</label>
        <input id="password" name="password" type="password" required minLength={8} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        <Button type="submit" block disabled={busy}>
          {busy ? 'Creating…' : 'Create account'}
        </Button>
      </form>
    </AuthCard>
  );
}
